import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Grid,
    Alert,
    CircularProgress,
    Breadcrumbs,
    Link,
} from '@mui/material';
import { ArrowBack, Save, Person, Storefront } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createFaq, getFaqById, updateFaq } from '../../services/Api';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const ModifyFaq = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        question: '',
        answer: '',
        type: 'vendor',
    });

    useEffect(() => {
        // 1. Try to get data from navigation state
        if (location.state?.faq) {
            const { faq } = location.state;

            setFormData({
                question: faq.question || '',
                answer: faq.answer || '',
                type: faq.type || 'vendor',
            });
            return;
        }

        // 2. Fallback to API if ID is present
        if (id) {
            const fetchFaq = async () => {
                setLoading(true);
                try {
                    const response = await getFaqById(id);
                    if (response.success) {
                        setFormData({
                            question: response.data.question || '',
                            answer: response.data.answer || '',
                            type: response.data.type || 'vendor',
                        });
                    } else {
                        setError(response?.message || 'Failed to fetch FAQ details');
                    }
                } catch (err) {
                    setError(err.response?.data?.message || err.message || 'An error occurred while fetching FAQ details');
                    console.error(err);
                } finally {
                    setLoading(false);
                }
            };
            fetchFaq();
        }
    }, [id, location.state]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.question || !formData.answer || formData.answer === '<p><br></p>') {
            setError('Please fill in both the question and the answer.');
            return;
        }

        if (!/^[a-zA-Z0-9]/.test(formData.question.trim())) {
            setError('Question cannot start with a special character.');
            return;
        }

        const strippedAnswer = formData.answer.replace(/(<([^>]+)>)/gi, "").replace(/&nbsp;/gi, "").trim();
        if (/^\d+$/.test(strippedAnswer)) {
            setError('Answer cannot contain only numeric values.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            const submitData = {
                question: formData.question,
                answer: formData.answer,
                type: formData.type,
            };

            let response;
            if (id) {
                response = await updateFaq(id, submitData);
            } else {
                response = await createFaq(submitData);
            }

            if (response.success) {
                setSuccess(true);
                setTimeout(() => navigate('/faqs'), 1500);
            } else {
                setError(response.message || `Failed to ${id ? 'update' : 'create'} FAQ`);
            }
        } catch (err) {
            setError(err.response?.data?.message || `An error occurred while ${id ? 'updating' : 'creating'} the FAQ`);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'clean']
        ],
    };

    return (
        <Box sx={{ p: 0 }}>
            {loading && !formData.question && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            )}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/faqs');
                        }}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <ArrowBack sx={{ mr: 0.5, fontSize: 'inherit' }} />
                        FAQs
                    </Link>
                    <Typography color="text.primary">{id ? 'Edit FAQ' : 'Create New FAQ'}</Typography>
                </Breadcrumbs>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {id ? 'Edit FAQ' : 'Create FAQ'}
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'text.border' }}>
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 3 }}>FAQ {id ? 'updated' : 'created'} successfully! Redirecting...</Alert>}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={4}>
                        {/* Content Section */}
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <TextField
                                    fullWidth
                                    size='small'
                                    label="Question"
                                    variant="outlined"
                                    value={formData.question}
                                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                                    placeholder="e.g. How do I register as a vendor?"
                                    required
                                />
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                                        Answer
                                    </Typography>
                                    <Box sx={{
                                        '& .ql-container': { height: '300px', borderRadius: '0 0 8px 8px' },
                                        '& .ql-toolbar': { borderRadius: '8px 8px 0 0' },
                                        '& .ql-editor': { fontSize: '1rem', fontFamily: 'inherit' }
                                    }}>
                                        <ReactQuill
                                            theme="snow"
                                            value={formData.answer}
                                            onChange={(content) => setFormData({ ...formData, answer: content })}
                                            modules={quillModules}
                                            placeholder="Provide a detailed answer here..."
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Sidebar Config Section */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <FormControl fullWidth size='small' variant="outlined">
                                    <InputLabel id="type-label">FAQ Category (Type)</InputLabel>
                                    <Select
                                        labelId="type-label"
                                        id="type"
                                        value={formData.type}
                                        label="FAQ Category (Type)"
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    >
                                        <MenuItem value="vendor">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Storefront fontSize="small" /> Vendor
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="user">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Person fontSize="small" /> User
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>

                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        size='small'
                                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                        disabled={loading}
                                        sx={{
                                            backgroundColor: 'primary.main',
                                            '&:hover': { backgroundColor: 'primary.dark' },
                                            py: 1.5,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {id ? 'Update FAQ' : 'Save FAQ'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        size='small'
                                        onClick={() => navigate('/faqs')}
                                        disabled={loading}
                                        sx={{ py: 1.2 }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default ModifyFaq;
